import { useEffect, useState } from 'react';
import { Menu, MenuButton } from '@airbus/components-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createPortal } from 'react-dom';
import LanguageSwitcher from './LanguageSwitcher';
import { t } from '../../../i18next';
import NotificationsCount from './NotificationsCount';
import styles from './header.module.css';
import ProfileDialog from '../../../features/landing_page/ProfileDialog';

type Props = {
  notificationCount: number;
  setNotificationPanelOpen: (open: boolean) => void;
  setShowSideNav: (fn: (v: boolean) => boolean) => void;
  userName: string;
  handleLogout: () => Promise<void>;
  setProfileOpen: (open: boolean) => void;
  profileOpen: boolean;
  roleNames?: any[];
};

export default function HeaderMobile({
  notificationCount,
  setNotificationPanelOpen,
  setShowSideNav,
  userName,
  handleLogout,
  setProfileOpen,
  profileOpen,
  roleNames = [],
}: Props) {
  const [drawerTopSlot, setDrawerTopSlot] = useState<HTMLElement | null>(null);
  const [drawerSlot, setDrawerSlot] = useState<HTMLElement | null>(null);

  const [langOpen, setLangOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.classList.add(styles.drawerScope, styles.drawerRight);
    return () => {
      document.body.classList.remove(
        styles.drawerOpen,
        styles.drawerPreparing,
        styles.drawerRight,
        styles.drawerScope,
      );
    };
  }, []);

  const ensureDrawerSlots = (root?: HTMLElement | null) => {
    if (typeof document === 'undefined') return;
    const SLOT_ID = 'mbd-mobile-extra';
    const TOP_ID = 'mbd-mobile-top';

    const prepare = (drawerRoot: HTMLElement) => {
      let top = drawerRoot.querySelector<HTMLElement>('#' + TOP_ID);
      if (!top) {
        top = document.createElement('div');
        top.id = TOP_ID;
        const firstNav = drawerRoot.querySelector<HTMLElement>('nav, [role="menu"], ul');
        if (firstNav?.parentElement) firstNav.insertAdjacentElement('beforebegin', top);
        else drawerRoot.insertBefore(top, drawerRoot.firstChild);
      }
      setDrawerTopSlot(top);

      let slot = drawerRoot.querySelector<HTMLElement>('#' + SLOT_ID);
      if (!slot) {
        slot = document.createElement('div');
        slot.id = SLOT_ID;
        const afterAnchor =
          drawerRoot.querySelector<HTMLElement>(
            'nav:last-of-type, [role="menu"]:last-of-type, ul:last-of-type',
          ) || (drawerRoot.lastElementChild as HTMLElement | null);
        if (afterAnchor?.parentElement) afterAnchor.insertAdjacentElement('afterend', slot);
        else drawerRoot.appendChild(slot);
      }
      setDrawerSlot(slot);
    };

    const target = root ?? document.querySelector<HTMLElement>('[class*="ds-drawer"]');
    if (target) prepare(target);
  };

  const waitForDrawer = (cb: (el: HTMLElement | null) => void, maxFrames = 24) => {
    let frames = 0;
    const tick = () => {
      const el = document.querySelector('[class*="ds-drawer"]') as HTMLElement | null;
      if (el) return cb(el);
      if (frames++ >= maxFrames) return cb(null);
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const openDrawer = () => {
    if (typeof document === 'undefined') return;

    document.body.classList.add(styles.drawerRight);
    document.body.classList.remove(styles.drawerOpen);
    document.body.classList.add(styles.drawerPreparing);

    setShowSideNav(() => true);

    waitForDrawer((drawer) => {
      if (drawer) {
        ensureDrawerSlots(drawer);
        drawer.getBoundingClientRect();
      }
      requestAnimationFrame(() => {
        document.body.classList.add(styles.drawerOpen);
        document.body.classList.remove(styles.drawerPreparing);
      });
    });
  };

  const closeDrawer = () => {
    if (typeof document === 'undefined') return;
    document.body.classList.remove(styles.drawerOpen);
    const CLOSE_MS = 280;
    setTimeout(() => setShowSideNav(() => false), CLOSE_MS);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handler = (e: MouseEvent) => {
      if (!document.body.classList.contains(styles.drawerOpen)) return;
      const drawer = document.querySelector('[class*="ds-drawer"]');
      if (drawer && !drawer.contains(e.target as Node)) closeDrawer();
    };
    document.addEventListener('pointerdown', handler);
    return () => document.removeEventListener('pointerdown', handler);
  }, []);

  return (
    <>
      {typeof document !== 'undefined' &&
        createPortal(
          <ProfileDialog open={profileOpen} setOpen={setProfileOpen} roleNames={roleNames} />,
          document.body,
        )}

      <div className={styles.mobileActions}>
        <Menu>
          <MenuButton
            variant='ghostNegative'
            onClick={() => setNotificationPanelOpen(true)}
            className={styles.noright}
          >
            <NotificationsCount count={notificationCount} iconSize={23} />
          </MenuButton>
        </Menu>

        <button className={styles.mobileTrigger} aria-label='Open menu' onClick={openDrawer}>
          <svg className={styles.hamburgerSvg} viewBox='0 0 24 24' aria-hidden='true'>
            <path d='M4 7h16' stroke='currentColor' strokeWidth='2.25' strokeLinecap='round' />
            <path d='M4 12h16' stroke='currentColor' strokeWidth='2.25' strokeLinecap='round' />
            <path d='M4 17h16' stroke='currentColor' strokeWidth='2.25' strokeLinecap='round' />
          </svg>
        </button>
      </div>

      {drawerTopSlot &&
        createPortal(
          <div className={styles.mobileHeadWrap}>
            <div className={styles.mobileHead}>
              <div className={styles.mobileTitle}>Menu</div>
              <button className={styles.mobileClose} aria-label='Close menu' onClick={closeDrawer}>
                <svg viewBox='0 0 24 24' width='22' height='22' aria-hidden='true'>
                  <path
                    d='M6 6l12 12M18 6L6 18'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </div>
          </div>,
          drawerTopSlot,
        )}

      {drawerSlot &&
        createPortal(
          <div className={styles.mobileList}>
            <div className={styles.mobileDivider} />

            <div
              className={styles.mobileRow}
              role='button'
              tabIndex={0}
              aria-expanded={langOpen}
              onClick={() => setLangOpen((v) => !v)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setLangOpen((v) => !v)}
            >
              <span className={styles.mobileIcon}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height='24px'
                  viewBox='0 -960 960 960'
                  width='24px'
                  fill='#0d2a66'
                >
                  <path d='m476-80 182-480h84L924-80h-84l-43-122H603L560-80h-84ZM160-200l-56-56 202-202q-35-35-63.5-80T190-640h84q20 39 40 68t48 58q33-33 68.5-92.5T484-720H40v-80h280v-80h80v80h280v80H564q-21 72-63 148t-83 116l96 98-30 82-122-125-202 201Zm468-72h144l-72-204-72 204Z' />
                </svg>
              </span>
              <span className={`${styles.mobileText} ${styles.mobileTextTitle}`}>
                <LanguageSwitcher />
              </span>
            </div>

            <div
              className={styles.mobileRow}
              role='button'
              tabIndex={0}
              aria-expanded={profileMenuOpen}
              onClick={() => setProfileMenuOpen((v) => !v)}
              onKeyDown={(e) =>
                (e.key === 'Enter' || e.key === ' ') && setProfileMenuOpen((v) => !v)
              }
            >
              <span className={styles.mobileIcon}>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  height='24px'
                  viewBox='0 -960 960 960'
                  width='24px'
                  fill='#0d2a66'
                >
                  <path d='M234-276q51-39 114-61.5T480-360q69 0 132 22.5T726-276q35-41 54.5-93T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 59 19.5 111t54.5 93Zm246-164q-59 0-99.5-40.5T340-580q0-59 40.5-99.5T480-720q59 0 99.5 40.5T620-580q0 59-40.5 99.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q53 0 100-15.5t86-44.5q-39-29-86-44.5T480-280q-53 0-100 15.5T294-220q39 29 86 44.5T480-160Zm0-360q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm0-60Zm0 360Z' />
                </svg>
              </span>
              <span className={styles.mobileText}>{userName}</span>
              <span className={styles.mobileChevron} aria-hidden='true'>
                <ExpandMoreIcon className={profileMenuOpen ? styles.chevRight : styles.rotate180} />
              </span>
            </div>

            {profileMenuOpen && (
              <div className={styles.subItem}>
                <div
                  className={styles.mobileRow}
                  onClick={() => {
                    closeDrawer();
                    setProfileOpen(true);
                  }}
                >
                  <span style={{ marginLeft: 15 }} className={styles.mobileText}>
                    {t('profile.profile')}
                  </span>
                </div>
              </div>
            )}

            <div
              className={styles.mobileRow}
              onClick={async () => {
                closeDrawer();
                await handleLogout();
              }}
            >
              <span className={styles.mobileIcon}>
                <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
                  <path
                    d='M10 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h4'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    fill='none'
                  />
                  <path
                    d='M14 16l4-4-4-4M8 12h10'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
              <span className={`${styles.mobileText} ${styles.mobileTextTitle}`}>
                {t('logout')}
              </span>
            </div>
          </div>,
          drawerSlot,
        )}
    </>
  );
}
